import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HeartPulse, Shirt } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SITE, HISTORY, HISTORY_EN, bnNum } from "@/constants/site";
import { useLang } from "@/lib/lang";

export default function Heritage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const { lang, t } = useLang();
  const PARAS = (lang === "en" ? HISTORY_EN : HISTORY).slice(0, 5);
  const CLOSING = (lang === "en" ? HISTORY_EN : HISTORY).slice(5);

  return (
    <section id="heritage" ref={ref} className="relative py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <Eyebrow>{t("her_eyebrow")}</Eyebrow>
              <h2 className="font-bengali font-extrabold text-3xl sm:text-5xl mt-4 leading-tight">
                {t("her_title_a")} <span className="gold-text">{t("her_title_b")}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <blockquote className="mt-8 border-l-2 border-[#D7261E] pl-5 font-bengali text-lg text-[#FDF6EA]/90 italic leading-relaxed">
                “{t("her_quote")}”
              </blockquote>
            </Reveal>
            <Reveal delay={0.15} className="mt-8 grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-[#F2B134]/20 bg-[#1E0A0E] p-5 spotlight">
                <HeartPulse className="text-[#D7261E]" size={22} />
                <p className="font-bengali font-bold mt-3">{t("her_card1_t")}</p>
                <p className="text-xs text-[#D1BBA7]/80 mt-1">{t("her_card1_s")}</p>
              </div>
              <div className="rounded-2xl border border-[#F2B134]/20 bg-[#1E0A0E] p-5 spotlight">
                <Shirt className="text-[#F2B134]" size={22} />
                <p className="font-bengali font-bold mt-3">{t("her_card2_t")}</p>
                <p className="text-xs text-[#D1BBA7]/80 mt-1">{t("her_card2_s")}</p>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden frame-gold mb-10">
              <motion.img
                style={{ y: imgY }}
                src="https://images.unsplash.com/photo-1728921957696-2f8317480abf?q=80&w=1600&auto=format&fit=crop"
                alt="পণ্ডাল আলোকসজ্জা"
                className="w-full h-64 sm:h-80 object-cover scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#120507] via-transparent to-transparent" />
              <p className="absolute bottom-4 left-5 text-xs uppercase tracking-[0.25em] text-[#F2B134]">{t("her_caption")}</p>
            </div>
          </Reveal>
          <div className="space-y-6 font-bengali text-base sm:text-lg leading-[1.9] text-[#D1BBA7]">
            {PARAS.map((p, i) => (
              <Reveal key={`${lang}-${i}`} delay={i * 0.04}>
                <p>{p}</p>
              </Reveal>
            ))}
            {CLOSING.map((p, i) => (
              <Reveal key={`${lang}-c${i}`} delay={i * 0.04}>
                <p className="text-[#FDF6EA]/90">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
